import React, { useEffect, useRef, useState } from "react";
import { AlertTriangle, Wifi } from "lucide-react";
import { toast } from "sonner";
import { apiBaseUrl } from "@/lib/api";

/**
 * SystemBanner — polls GET /api/ping every 30 s. Renders an amber dismissable
 * banner + fires a single toast when the control plane stops responding.
 * Toast never re-fires on consecutive failures (spam avoidance); only when
 * the state transitions offline→online and back.
 */
const POLL_INTERVAL_MS = 30_000;
const REQUEST_TIMEOUT_MS = 8_000;

export default function SystemBanner() {
    const [offline, setOffline] = useState(false);
    const [lastCheck, setLastCheck] = useState(null);
    const wasOffline = useRef(false);

    useEffect(() => {
        let cancelled = false;
        const base = apiBaseUrl();
        if (!base) return undefined;

        const ping = async () => {
            const controller = new AbortController();
            const t = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
            try {
                const resp = await fetch(`${base}/api/ping`, { signal: controller.signal });
                if (cancelled) return;
                const ok = resp.ok;
                setOffline(!ok);
                setLastCheck(new Date());
                if (!ok && !wasOffline.current) {
                    wasOffline.current = true;
                    toast.error("MoovBytes control plane unreachable", {
                        description: "Live updates are paused until connectivity is restored.",
                    });
                } else if (ok && wasOffline.current) {
                    wasOffline.current = false;
                    toast.success("MoovBytes control plane reconnected");
                }
            } catch {
                if (cancelled) return;
                setOffline(true);
                setLastCheck(new Date());
                if (!wasOffline.current) {
                    wasOffline.current = true;
                    toast.error("MoovBytes control plane unreachable", {
                        description: "Live updates are paused until connectivity is restored.",
                    });
                }
            } finally {
                clearTimeout(t);
            }
        };

        ping(); // immediate on mount
        const iv = setInterval(ping, POLL_INTERVAL_MS);
        return () => { cancelled = true; clearInterval(iv); };
    }, []);

    if (!offline) return null;

    return (
        <div
            className="flex items-center gap-3 border-b border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 px-6 py-2 text-[12px] text-[hsl(var(--warning))]"
            role="alert"
            data-testid="system-banner-offline"
        >
            <AlertTriangle size={14} className="shrink-0" />
            <div className="flex-1">
                <span className="font-semibold">Control plane unreachable.</span>{" "}
                <span className="text-muted-foreground">
                    Retrying every 30 s — agent telemetry, jobs and live updates are paused.
                    {lastCheck && ` Last check: ${lastCheck.toLocaleTimeString()}.`}
                </span>
            </div>
            <Wifi size={12} className="shrink-0 animate-pulse" />
        </div>
    );
}
